import { useContext, useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "react-bootstrap";
import { getManagerUrl } from "@/config";

import MultiTabComponent, { IMultiTabComponentConfig } from "../../../manager/components/multi-tab-component/multi-tab-component";
import Page from "../../../manager/components/page/page";
import ConfirmationPopup from "../../../shared/components/confirmation-popup/confirmation-popup";
import AuthContext from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import { failedToast, getAllData, postSingle } from "../../../shared/utils/api";
import { createUUID } from "../../../shared/utils/utils";
import "./datamodel-report-new.scss";
import { usePrompt } from "../../../shared/utils/use-prompt";
import DatamodelReportSetup from "./datamodel-report-setup";
import DatamodelReportConfiguration from "./datamodel-report-configuration";




export interface IQueryFilter {
  field: string;
  op: string;
  value: string;
  field_type: string;
}

export interface IDatamodelReportData {
  report_id: string;
  report_name: string;
  datamodel_type: string;
  datamodel_id: string | undefined;
  group_by: string;
  group_by_datatype: string;
  query_filters: IQueryFilter[] | undefined;
  columns:
  {
    field: string;
    field_type: string;
    agg: string;
    aggregator_title: string;
    agg_datatype: string;
  }[] | undefined;
}

const DatamodelReportNew = () => {
  const params = useParams();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const [initialData, setInitialData] = useState<IDatamodelReportData>({
    report_id: "",
    report_name: "",
    datamodel_type: "",
    datamodel_id: "",
    group_by: "",
    group_by_datatype: "",
    query_filters: undefined,
    columns: undefined
  });
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const { flag, setFlag, next } = usePrompt(isDirty);
  const formRef = useRef<HTMLFormElement>(null);
  const { sendRequest: postDatamodelData, status: postDatamodelDataStatus, data: postDatamodelDataResult, error: postDatamodelDataError } = useHttp(postSingle, true, authCtx.token, "datamodelReport", "platform");

  const [datamodelReportData, setDatamodelReportData] = useState<IDatamodelReportData>(
    {
      report_id: "",
      report_name: "",
      datamodel_type: "",
      datamodel_id: "",
      group_by: "",
      group_by_datatype: "",
      query_filters: undefined,
      columns: undefined
    }
  );

  const { sendRequest: getReportConfiguration, status: getReportConfigurationStatus, data: getReportConfigurationData, error: errorGetReportConfigurationData } = useHttp(getAllData, true, authCtx.token, "datamodelReportConfiguration");
  const hasManager = getManagerUrl() !== "";

  const isReportConfigurationVisible = datamodelReportData?.datamodel_type === "alertcase" && hasManager
    ? datamodelReportData.report_name !== ""
    : (datamodelReportData.datamodel_type !== "" && datamodelReportData.datamodel_id !== undefined && datamodelReportData.report_name !== "");

  useEffect(() => {
    if (params.id && params.id !== "new") {
      getReportConfiguration(`report_config/${params.id}`);
    }
  }, [params.id]);

  useEffect(() => {
    if (initialData && (
      datamodelReportData.report_id !== initialData.report_id ||
      datamodelReportData.report_name !== initialData.report_name ||
      datamodelReportData.datamodel_type !== initialData.datamodel_type ||
      datamodelReportData.datamodel_id !== initialData.datamodel_id ||
      datamodelReportData.group_by !== initialData.group_by ||
      JSON.stringify(datamodelReportData.query_filters) !== JSON.stringify(initialData.query_filters) ||
      JSON.stringify(datamodelReportData.columns) !== JSON.stringify(initialData.columns)
    )) {
      setIsDirty(true);
    }
  }, [datamodelReportData, initialData]);

  useEffect(() => {
    if (postDatamodelDataStatus === "completed" && !postDatamodelDataError) {
      navigate("/datamodel-reports");
    }
  }, [postDatamodelDataStatus, postDatamodelDataResult]);

  useEffect(() => {
    if (getReportConfigurationStatus === "completed" && !errorGetReportConfigurationData && getReportConfigurationData) {
      const loadedData = {
        report_id: params.id || "",
        report_name: getReportConfigurationData.name || "",
        datamodel_id: getReportConfigurationData.datamodel_id || "",
        datamodel_type: getReportConfigurationData.datamodel_type || "",
        group_by: getReportConfigurationData.group_by || "",
        group_by_datatype: getReportConfigurationData.group_by_datatype || "",
        query_filters: getReportConfigurationData.query_filters || [],
        columns: getReportConfigurationData.columns || []
      };
      setDatamodelReportData(loadedData);
      setInitialData(loadedData);
    } else if (params.id === "new") {
      setInitialData(datamodelReportData);
    }
  }, [getReportConfigurationStatus, params.id]);

  useEffect(() => {
    if (getReportConfigurationStatus === "completed" && !errorGetReportConfigurationData && getReportConfigurationData) {
      setDatamodelReportData({
        ...getReportConfigurationData,
        report_id: params.id || "",
        report_name: getReportConfigurationData.name || "",
        datamodel_id: getReportConfigurationData.datamodel_id || "",
        datamodel_type: getReportConfigurationData.datamodel_type || "",
        group_by: getReportConfigurationData.group_by || "",
        group_by_datatype: getReportConfigurationData.group_by_datatype || "",
        query_filters: getReportConfigurationData.query_filters || [],
        columns: getReportConfigurationData.columns || []
      });
    }
  }, [getReportConfigurationStatus]);

  const validateForm = (): boolean => {
    if (!formRef.current) {
      return false;
    }

    const isValid = formRef.current.checkValidity();

    if (!isValid) {
      formRef.current.reportValidity();
    }

    return isValid;
  };

  const validateReportData = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!datamodelReportData.report_name.trim()) {
      errors.push("Report name is required");
    }

    if (!datamodelReportData.datamodel_type) {
      errors.push("Datamodel type is required");
    }

    if (datamodelReportData.datamodel_type !== "alertcase" && !datamodelReportData.datamodel_id && hasManager) {
      errors.push("Datamodel ID is required");
    }

    if (!datamodelReportData.group_by) {
      errors.push("Group by column is required");
    }

    datamodelReportData.query_filters?.forEach((filter, index) => {
      const hasAnyField = filter.field || filter.op || filter.value;

      if (hasAnyField) {
        if (!filter.field) {
          errors.push(`Query filter ${index + 1}: Filter column is required`);
        }
        if (!filter.op) {
          errors.push(`Query filter ${index + 1}: Filter operator is required`);
        }
        if (!filter.value) {
          errors.push(`Query filter ${index + 1}: Filter value is required`);
        }
      }
    });

    datamodelReportData.columns?.forEach((column, index) => {
      const hasAnyField = column.field || column.agg || column.aggregator_title;

      if (hasAnyField) {
        if (!column.field) {
          errors.push(`Aggregation ${index + 1}: Aggregation column is required`);
        }
        if (!column.agg) {
          errors.push(`Aggregation ${index + 1}: Aggregation operator is required`);
        }
        if (!column.aggregator_title) {
          errors.push(`Aggregation ${index + 1}: Aggregation column title is required`);
        }
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const handleFormSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    setIsDirty(false);
    if (!validateForm()) {
      return;
    }

    const validation = validateReportData();
    if (!validation.isValid) {
      console.error("Validation errors:", validation.errors);
      validation.errors.forEach(error => {
        console.error(error);
        failedToast(error);
      });
      return;
    }

    const data = { ...datamodelReportData };
    if (data.report_id === "") {
      data.report_id = createUUID();
    }
    if (!data.columns) {
      data.columns = [];
    }
    if (!data.query_filters) {
      data.query_filters = [];
    }
    postDatamodelData("generate_report_config", data);
  };

  const multitabConfig: IMultiTabComponentConfig = {
    activeTab: "reportSetup",
    tabs: [{
      name: "Report setup",
      key: "reportSetup",
      content: <DatamodelReportSetup
        datamodelReportData={datamodelReportData}
        onDataChange={setDatamodelReportData}
      />
    },
    {
      name: "Report configuration",
      key: "reportConfiguration",
      content: <DatamodelReportConfiguration
        datamodelReportData={datamodelReportData}
        onDataChange={setDatamodelReportData}
      />,
      isVisible: () => isReportConfigurationVisible
    }]
  };

  return (
    <Page title={{
      text: params.id && params.id !== "new" ? "Edit report configuration" : "Create report configuration",
      onTitleEdited: undefined,
      placeholder: "Report configuration"
    }}>
      <div className="datamodelReportNew">
        <form ref={formRef} noValidate>
          <MultiTabComponent {...multitabConfig} />
        </form>
        <div className="datamodelReportNew-button-group">
          <Button
            type="submit"
            onClick={handleFormSubmit}
            className="datamodelReportNew-createButton"
          >
            {params.id ? "Update" : "Create"}
          </Button>
        </div>
        {flag && <ConfirmationPopup show={flag}
          handleSaveClick={handleFormSubmit}
          handlePopupClosed={() => {
            setFlag(false);
          }}
          handleContinue={() => { next(); setFlag(false); }} />
        }
      </div>
    </Page>
  );
};

export default DatamodelReportNew;